/**
 * SOLID Review
 * S — Strong. One use case: the code screen — a code accepted signs the tab in.
 * O — Strong. The round trip runs it; what is allowed is the Domain's.
 * L — Strong. Implements its use-case role and the round trip's three strategies exactly.
 * I — Strong. `execute`, and the three strategies the round trip asks for.
 * D — Strong. The decisions and auth-server ports and the round trip, injected.
 */
import type { IContextRequest } from '../ports/IContextRequest';
import type { IPropsClientInput } from '../../domain/ports/schemas/ClientChanges';
import type { ICommandSignedIn, ICommandVerifyCode, IPayloadSignedIn, IPayloadVerifyCode } from '../../domain/ports/schemas/AuthMoves';
import type { TPortOutcome } from '../ports/PortResult';
import type { IUseCase } from '../uml-components/IUseCase';
import type { TServerRoundTrip, TAnswered } from '../shared/ServerRoundTrip';
import type { IConfigDepAuth } from './IConfigDepAuth';

/** The code screen (FE3): a code accepted signs the tab in; the token stays in Infrastructure. */
export class VerifyCodeUseCase implements IUseCase<ICommandVerifyCode, TPortOutcome>, TServerRoundTrip<IPropsClientInput, ICommandVerifyCode, IPayloadVerifyCode, TAnswered<IPayloadSignedIn>> {
  constructor(private readonly deps: IConfigDepAuth) {}

  execute(ctx: IContextRequest, command: ICommandVerifyCode): Promise<TPortOutcome> {
    return this.deps.trip.run(ctx, command, this);
  }

  plan(ctx: IContextRequest, input: IPropsClientInput, command: ICommandVerifyCode) {
    return this.deps.decisions.planVerifyCode(ctx, input, command);
  }

  send(ctx: IContextRequest, plan: IPayloadVerifyCode) {
    return this.deps.auth.verifyCode(ctx, plan);
  }

  answer(ctx: IContextRequest, input: IPropsClientInput, _plan: IPayloadVerifyCode, verified: TAnswered<IPayloadSignedIn>) {
    const signedIn: ICommandSignedIn = { identity: verified.value };
    return this.deps.decisions.signedIn(ctx, input, signedIn);
  }
}
